import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../WorkflowTask.scss';
import HrbTranslate from '../../../../../components/language/HrbTranslate'
import Lang from '../../../../../components/language/services/Lang'
import AccordionWithStatus from 'components/library/accordion/AccordionWithStatus';
import Checkbox from 'components/library/buttons/Checkbox';
import RadioButtonGroup from 'components/library/buttons/RadioButton/RadioButtonGroup';
import Button from '../../../../../components/library/buttons/Button';
import RadioButton from 'components/library/buttons/RadioButton/RadioButton';
import SectionDivider from 'components/library/common/SectionDivider';
import WorkflowTaskModal from 'components/library/workflow-tasks/WorkflowTaskModal';
import infoMark from '../../../../../resources/images/info.svg';
import DateTime from 'components/library/common/DateTime';
import { createAuthorizationMethod } from '../../../services/documentReview/DocumentReviewUtils';
import { isAuthorizationPassed, isAuthorizationBypassed, hasSentAuthorizationRequest, 
    isChoiceMyAccount, isChoiceSignaturePage, 
    isAuthorizationCompleted, getRacExpiryDate, cleanAuthorizationInfo,
    AUTHORIZATION_PASS, AUTHORIZATION_BYPASS, SEND_AUTHORIZATION_REQUEST, 
    CHOICE_MY_ACCOUNT, CHOICE_SIGNATURE_PAGE } from '../../../../../strategies/DocumentReviewStrategy';
import DoxUploader from '../../dox/Uploader/doxUploader';
import documentApi from 'services/api/documentApi';
import { useTaskStatus } from '../../hooks/useTaskStatusHook';
import SignedDocumentSection from 'components/signedDocumentInformation/SignedDocumentSection';
import { trackEvent, HeapEvents } from '../../../../../services/util/AnalyticsUtil';
import { isValidSignaturePageFileName, getExpectedSignaturePageFileName } from '../../../../../strategies/SignaturePageValidation';
import { Error as ErrorIcon } from '@mui/icons-material';

const DocumentReviewAuthorizationTask = (props) => {

    const { elementId, title, translateTitle, groupLabel, isInProgress, filer, authorizationInfo, saveAuthorizationData, completeAuthorization, ticket, filerName, signaturePageWorkflowTask, resetSignaturePage } = props;

    // State Variables
    const [isCompleting, setIsCompleting] = useState(false);
    const [authorizationSelected, setAuthorizationSelected] = useState(null);
    const [choiceSelected, setChoiceSelected] = useState(null);
    const [hasMessagedClient, setHasMessagedClient] = useState(false);
    const [hasClientCompletedAuthorization, setHasClientCompletedAuthorization] = useState(false);
    const [showConfirmAuthorizationModal, setShowConfirmAuthorizationModal] = useState(false);
    const [uploaderOpen, setUploaderOpen] = useState(false);
    const [signaturePageDocumentId, setSignaturePageDocumentId] = useState(null);
    const [hasSubmittedSignaturePageToRac, setHasSubmittedSignaturePageToRac] = useState(false);
    const [showSignaturePageWarning, setShowSignaturePageWarning] = useState(false);
    const isAuthorizationComplete = isAuthorizationCompleted(authorizationInfo);
    const [modalConfig, setModalConfig] = useState({
        titleKey: 'DocumentReviewTask.ConfirmAuthorization.Title',
        contentKeys: ['DocumentReviewTask.ConfirmAuthorization.ConfirmSelectionDescription'],
        actionKey: 'DocumentReviewTask.ConfirmAuthorization.Confirm',
        action: {
            type: 'authorization-type',
            value: null
        }
    });
    const [, signaturePageTaskCompletion] = useTaskStatus(ticket, [signaturePageWorkflowTask]);    

    const getSignaturePageTaskData = () => {
        if(signaturePageTaskCompletion.length <= 0) {
            return;
        }
        
        return  JSON.parse(signaturePageTaskCompletion[0].completionData);
    }
    const [signaturePageTaskData, setSignaturePageTaskData] = useState(getSignaturePageTaskData());

    const api = documentApi.create(null);
    
    const getFullFileName = (doc) => {
        if (doc.fileName.toLowerCase().endsWith(doc.fileType.toLowerCase())) {
            return doc.fileName;
        }
        return `${doc.fileName}${doc.fileType}`;
    };
    
    const getAuthorizationData = () => {
        if(authorizationInfo) {
            return authorizationInfo;
        }

        return createAuthorizationMethod(null, null, null, false, null, false);
    }
    
    const onUploadsComplete = async (filesWereUploaded) => {
        if (filesWereUploaded) {
            var signaturePage = await getSignaturePage();
            if(signaturePage) {
                setIsCompleting(true);
                setSignaturePageDocumentId(signaturePage.documentId);
                setShowSignaturePageWarning(false);
            } else {
                const documents = await api.getFiles(ticket.id);
                const clientFirstName = filerName.split(' ')[0];
                const hasInvalidFile = documents.fileList.some(doc => 
                    doc.documentStatus === 'Uploaded' && 
                    !isValidSignaturePageFileName(getFullFileName(doc), clientFirstName)
                );
                if (hasInvalidFile) {
                    setShowSignaturePageWarning(true);
                }
            }
        }

        setUploaderOpen(false);
    }

    const getSignaturePage = async () => {
        const documents = await api.getFiles(ticket.id);
        const clientFirstName = filerName.split(' ')[0];
        return documents.fileList.find(doc => 
            doc.documentStatus === 'Uploaded' && 
            isValidSignaturePageFileName(getFullFileName(doc), clientFirstName)
        );
    }

    const onCloseUploader = () => {
        setUploaderOpen(false);
    }

    useEffect(() => {
        if(authorizationInfo) {
            setAuthorizationSelected(authorizationInfo.authorizationType);
            setChoiceSelected(authorizationInfo.choice);
            setHasMessagedClient(authorizationInfo.hasMessagedClient);
            setHasClientCompletedAuthorization(authorizationInfo.hasClientCompletedAuthorization);
            setSignaturePageDocumentId(authorizationInfo.signaturePage);
            setHasSubmittedSignaturePageToRac(authorizationInfo.hasSubmittedSignaturePageToRac);

            if(isAuthorizationComplete) {
                completeAuthorization(true);
            } else {
                completeAuthorization(false);
            }
        } else {
            setAuthorizationSelected(null);
            setChoiceSelected(null);
            setHasMessagedClient(false);
            setHasClientCompletedAuthorization(false);
            setSignaturePageDocumentId(null);
            setHasSubmittedSignaturePageToRac(false);
            completeAuthorization(false);
        }
    }, [authorizationInfo])

    useEffect(() => {
        if(!isCompleting) return;
        const authorizationInfo = getAuthorizationData();
        if(authorizationInfo.authorizationType !== authorizationSelected) {
            authorizationInfo.authorizationType = authorizationSelected;
        }
        if(hasSentAuthorizationRequest(authorizationSelected)) {
            if(authorizationInfo.choice !== choiceSelected) {
                authorizationInfo.choice = choiceSelected;
                authorizationInfo.choiceTimestamp = choiceSelected !== null ? new Date().toISOString() : null;
            }
            if(isChoiceMyAccount(choiceSelected)) {
                if(authorizationInfo.hasMessagedClient !== hasMessagedClient) {
                    authorizationInfo.hasMessagedClient = hasMessagedClient;
                    authorizationInfo.messageTimestamp = hasMessagedClient ? new Date().toISOString() : null;
                }
                authorizationInfo.hasClientCompletedAuthorization = hasClientCompletedAuthorization;
                setSignaturePageDocumentId(null);
                setHasSubmittedSignaturePageToRac(false);
                setSignaturePageTaskData(null);
            }
            else if(isChoiceSignaturePage(choiceSelected)) {
                if(authorizationInfo.signaturePage !== signaturePageDocumentId) {
                    authorizationInfo.signaturePage = signaturePageDocumentId;
                    authorizationInfo.signaturePageUploadTimestamp = signaturePageDocumentId !== null ? new Date().toISOString() : null;
                }
                if(authorizationInfo.hasSubmittedSignaturePageToRac !== hasSubmittedSignaturePageToRac) {
                    authorizationInfo.hasSubmittedSignaturePageToRac = hasSubmittedSignaturePageToRac;
                }
                setHasMessagedClient(false);
                setHasClientCompletedAuthorization(false);
            }
        }
        else
        {
            setChoiceSelected(null);
            setHasMessagedClient(false);
            setHasClientCompletedAuthorization(false);
            setSignaturePageDocumentId(null);
            setHasSubmittedSignaturePageToRac(false);
            setSignaturePageTaskData(null);
        }
        cleanAuthorizationInfo(authorizationInfo);
        saveAuthorizationData(authorizationInfo);
        completeAuthorization(isAuthorizationCompleted(authorizationInfo));
        setIsCompleting(false);
    }, [authorizationSelected, choiceSelected, hasMessagedClient, hasClientCompletedAuthorization, signaturePageDocumentId, hasSubmittedSignaturePageToRac])

    const handleAuthorizationChange = (value) => {
        setIsCompleting(true);
        if(!shouldConfirmAuthorization(value)) {
            setAuthorizationSelected(value);
            trackAuthorizationChange(value);
            return;
        }
        setModalConfig({
            titleKey: 'DocumentReviewTask.ConfirmAuthorization.Title',
            contentKeys: ['DocumentReviewTask.ConfirmAuthorization.ConfirmSelectionDescription'],
            actionKey: 'DocumentReviewTask.ConfirmAuthorization.Confirm',
            action: {
                type: 'authorization-type',
                value: value
            }
        });
        setShowConfirmAuthorizationModal(true);
    };

    const handleChoiceChange = (value) => {
        setIsCompleting(true);
        if(!shouldConfirmChoice()) {
            setChoiceSelected(value);
            trackAuthorizationChoiceChange(value);
            return;
        }
        
        setModalConfig({
            titleKey: 'DocumentReviewTask.ConfirmChoice.Title',
            contentKeys: ['DocumentReviewTask.ConfirmChoice.ConfirmSelectionDescription'],
            actionKey: 'DocumentReviewTask.ConfirmChoice.Confirm',
            action: {
                type: 'authorization-choice',
                value: value
            }
        });
        setShowConfirmAuthorizationModal(true);
    };

    const shouldConfirmAuthorization = (authorizationType) => isAuthorizationPassed(authorizationType) || isAuthorizationBypassed(authorizationType);

    const shouldConfirmChoice = () => choiceSelected != null;

    const toggleHasMessagedClient = (_) => {
        setIsCompleting(true);
        setHasMessagedClient(prev => !prev);
    };

    const toggleHasClientCompletedAuthorization = (_) => {
        setIsCompleting(true);
        setHasClientCompletedAuthorization(prev => toggleAndTrackChange(prev, HeapEvents.DOCUMENT_REVIEW_CLIENT_ACCEPTED));
    };

    const toggleHasSubmittedSignaturePageToRac = (_) => {
        setIsCompleting(true);
        setHasSubmittedSignaturePageToRac(prev => toggleAndTrackChange(prev, HeapEvents.DOCUMENT_REVIEW_SIGNATURE_PAGE_SUBMITTED));
    };

    const toggleAndTrackChange = (currentState, eventId) => {
        var updatedState = !currentState;
        if(updatedState) {
            trackEvent(eventId, ticket.id);
        }
        return updatedState;
    }

    const createLink = (urlKey, titleKey) => (
        <a href={Lang.getCurrentValue(urlKey)} target='_blank' rel='noreferrer noopener'>
            <HrbTranslate text={titleKey} />
        </a>
    )

    const handleResetSignaturePageClick = () => {
        setModalConfig({
            titleKey: 'DocumentReviewTask.ResetSignaturePageConfirmation.Title',
            contentKeys: ['DocumentReviewTask.ResetSignaturePageConfirmation.Description'],
            actionKey: 'DocumentReviewTask.ResetSignaturePageConfirmation.Confirm',
            action: {
                type: 'reset-signature-page',
                value: true
            }
        });
        setShowConfirmAuthorizationModal(true);
    };

    const handleConfirmation = async (action) => {
        if(action.type === 'authorization-type' ) {
            setAuthorizationSelected(action.value);
            trackAuthorizationChange(action.value);
            setChoiceSelected(null);
            setShowSignaturePageWarning(false);
        }
        else if(action.type === 'authorization-choice') {
            setChoiceSelected(action.value);
            trackAuthorizationChoiceChange(action.value);
            setHasMessagedClient(false);
            setHasClientCompletedAuthorization(false);
            setShowSignaturePageWarning(false);
        }
        else if(action.type === 'reset-signature-page') {
            setIsCompleting(true);
            setShowSignaturePageWarning(false);
            resetSignaturePage();
        }
        setShowConfirmAuthorizationModal(false);
    }

    const handleCancel = (_) => {
        setShowConfirmAuthorizationModal(false);
    }

    const trackAuthorizationChange = (authorization) => {
        if(isAuthorizationPassed(authorization)) {
            trackEvent(HeapEvents.DOCUMENT_REVIEW_AUTHORIZATION_PASS, ticket.id);
        }
        else if(isAuthorizationBypassed(authorization)) {
            trackEvent(HeapEvents.DOCUMENT_REVIEW_AUTHORIZATION_BYPASS, ticket.id);
        }
        else if(hasSentAuthorizationRequest(authorization)) {
            trackEvent(HeapEvents.DOCUMENT_REVIEW_SEND_AUTHORIZATION_REQUEST, ticket.id);
        }
    }

    const trackAuthorizationChoiceChange = (choice) => {
        if(isChoiceMyAccount(choice)) {
            trackEvent(HeapEvents.DOCUMENT_REVIEW_MYACCOUNT, ticket.id);
        }
        else if(isChoiceSignaturePage(choice)) {
            trackEvent(HeapEvents.DOCUMENT_REVIEW_SIGNATURE_PAGE, ticket.id);
        }
    }

    return (
        <AccordionWithStatus
            elementId={elementId}
            translateTitle={translateTitle}
            title={title}
            isComplete={isAuthorizationComplete}
            defaultExpanded={!isAuthorizationComplete}
            expanded={!isAuthorizationComplete} >
            <RadioButtonGroup
                elementId='document-review-authorization-confirmation-radio-group'
                name='document-review-authorization-confirmation'
                defaultValue={null}
                labelKey='document-review-authorization-confirmation'
                groupLabel={groupLabel}
                groupLabelClassName='bold'
            >
                <RadioButton
                    elementId='document-review-authorization-pass-radio'
                    value={AUTHORIZATION_PASS}
                    text='DocumentReviewTask.AuthorizationPassRadioButton'
                    isSelected={isAuthorizationPassed(authorizationSelected)}
                    onChange={handleAuthorizationChange}
                    disabled={!isInProgress}
                />
                <RadioButton
                    elementId='document-review-authorization-bypass-radio'
                    value={AUTHORIZATION_BYPASS}
                    text='DocumentReviewTask.AuthorizationBypassRadioButton'
                    isSelected={isAuthorizationBypassed(authorizationSelected)}
                    onChange={handleAuthorizationChange}
                    disabled={!isInProgress}
                />
                <RadioButton
                    elementId='document-review-send-authorization-request-radio'
                    value={SEND_AUTHORIZATION_REQUEST}
                    text='DocumentReviewTask.SendAuthorizationRequestRadioButton'
                    isSelected={hasSentAuthorizationRequest(authorizationSelected)}
                    onChange={handleAuthorizationChange}
                    disabled={!isInProgress}
                />
            </RadioButtonGroup>
            {hasSentAuthorizationRequest(authorizationSelected) && (
            <>
                <SectionDivider classNames='no-padding h1' />
                <p className='p2'>
                    <HrbTranslate text='DocumentReviewTask.SendAuthorizationRequestTaskDescription' 
                    params={{ ProSupportArticle: createLink('DocumentReviewTask.ProSupportArticleLink', 'DocumentReviewTask.ProSupportArticle')}} />
                </p>
                <RadioButtonGroup
                    elementId='document-review-authorization-confirmation-radio-group'
                    name='document-review-client-authorization-choice'
                    defaultValue={null}
                    labelKey='document-review-client-authorization-choice'
                    groupLabel='DocumentReviewTask.StepRequiredToCompleteAuthorization'
                >
                    <RadioButton
                        elementId='document-review-authorization-client-choice-myaccount-radio'
                        value={CHOICE_MY_ACCOUNT}
                        text='DocumentReviewTask.ClientAuthorizedWithMyAccount'
                        isSelected={isChoiceMyAccount(choiceSelected)}
                        onChange={handleChoiceChange}
                        disabled={!isInProgress}
                    />
                    <RadioButton
                        elementId='document-review-authorization-client-choice-signature-page-radio'
                        value={CHOICE_SIGNATURE_PAGE}
                        text='DocumentReviewTask.ClientAuthorizedWithSignaturePage'
                        isSelected={isChoiceSignaturePage(choiceSelected)}
                        onChange={handleChoiceChange}
                        disabled={!isInProgress}
                    />
                </RadioButtonGroup>
                <p className='p2'>
                    <HrbTranslate text='DocumentReviewTask.AddReferenceNumber' 
                    params={{ InternalNotes: createLink('DocumentReviewTask.InternalNotesLink', 'DocumentReviewTask.InternalNotes')}} />
                </p>
                {isChoiceMyAccount(choiceSelected) && (
                <>
                    <p className='p2 info-message'>
                        <img src={infoMark} />
                        <span>
                            <HrbTranslate text='DocumentReviewTask.ClientAcceptAndFinalizeRequestInfo'
                            params={{ date: <DateTime value={getRacExpiryDate(authorizationInfo)} dateAndTime/> }} />
                        </span>
                    </p>
                    <SectionDivider classNames='no-padding h1' />
                    <p className='p2'>
                        <HrbTranslate text='DocumentReviewTask.SendAcceptAuthorizationRequestTemplate' 
                        params={{ AcceptAuthorizationRequestInstruction: createLink('DocumentReviewTask.AcceptAuthorizationRequestInstructionLink', 'DocumentReviewTask.AcceptAuthorizationRequestInstruction')}} />
                    </p>
                    <Checkbox textKey={`DocumentReviewTask.SentMessageTemplateToClientCheckbox`}
                        checked={hasMessagedClient}
                        onChecked={toggleHasMessagedClient}
                        elementId={'document-review-message-template-client-checkbox-' + filer}
                        disabled={!isInProgress} />
                    <div>
                        <p className='p2'>
                            <HrbTranslate text='DocumentReviewTask.SentOn' />
                        </p>
                        <p className='p2'>
                        {authorizationInfo.messageTimestamp ? <DateTime boldValue={true} value={authorizationInfo.messageTimestamp} dateAndTime/>
                                                            : '---'
                        }
                        </p>
                    </div>
                    <Checkbox textKey={`DocumentReviewTask.AcceptAuthorizationRequestCheckbox`}
                        checked={hasClientCompletedAuthorization}
                        onChecked={toggleHasClientCompletedAuthorization}
                        elementId={'document-review-accept-authorization-request-checkbox-' + filer} 
                        disabled={!isInProgress || !hasMessagedClient} />
                </>)}
                {isChoiceSignaturePage(choiceSelected) && (
                <>
                    <p className='p2 info-message'>
                        <img src={infoMark} />
                        <span>
                            <HrbTranslate text='DocumentReviewTask.TaxProAcceptAndFinalizeRequestInfo'
                            params={{ date: <DateTime value={getRacExpiryDate(authorizationInfo)} dateAndTime/> }} />
                        </span>
                    </p>
                    <SectionDivider classNames='no-padding h1' />
                    {signaturePageTaskData && 
                        <>
                            <SignedDocumentSection elementId={'signature-page-document-section-' + filer}
                                title='DocumentReviewTask.SignedOn'
                                uploaded={signaturePageTaskData.timestamp}
                                ticketId={ticket.id}
                                documentType={signaturePageWorkflowTask}
                            />
                            <p className='p2'>
                                <HrbTranslate text='DocumentReviewTask.SubmitSignaturePageDescription' 
                                params={{ GoToRac: createLink('DocumentReviewTask.GoToRacLink', 'DocumentReviewTask.GoToRac'),
                                 InternalNotes: createLink('DocumentReviewTask.InternalNotesLink', 'DocumentReviewTask.InternalNotes')}} />
                            </p>
                            <p className='p2'><HrbTranslate text='DocumentReviewTask.RacSubmissionReviewNote' /></p>
                            
                            <Checkbox textKey={`DocumentReviewTask.SubmittedSignaturePageCheckbox`}
                                checked={hasSubmittedSignaturePageToRac}
                                onChecked={toggleHasSubmittedSignaturePageToRac}
                                elementId={'document-review-has-submitted-signature-page-checkbox-' + filer} 
                                disabled={!isInProgress} />
                        </>
                    }
                    {!signaturePageTaskData &&
                    <>
                        <p className='p2'>
                            <HrbTranslate text='DocumentReviewTask.SearchForClientsAuthorizationRequestDescription' 
                            params={{ CompleteAuthorizationSignaturePageInstruction: createLink('DocumentReviewTask.CompleteAuthorizationSignaturePageInstructionLink', 'DocumentReviewTask.CompleteAuthorizationSignaturePageInstruction')}} />
                        </p>
                        <p className='p2'><HrbTranslate text='DocumentReviewTask.SignaturePageReviewNote' /></p>
                        {!authorizationInfo.signaturePage && <Button
                            elementId='document-review-upload-signature-page-btn'
                            labelKey='DocumentReviewTask.UploadSignaturePage'
                            buttonStyles='secondary center'
                            onClick={() => setUploaderOpen(true)}
                            disabled={!isInProgress} /> }
                        {showSignaturePageWarning && !authorizationInfo.signaturePage && (
                            <div className='signature-page-warning'>
                                <ErrorIcon className='warning-icon' />
                                <div className='warning-content'>
                                    <p>
                                        <HrbTranslate text='DocumentReviewTask.SignaturePageUploadWarning.Title' />
                                    </p>
                                    <ul className='warning-file-list'>
                                        <li><HrbTranslate text='DocumentReviewTask.SignaturePageUploadWarning.File1' /></li>
                                        <li><HrbTranslate text='DocumentReviewTask.SignaturePageUploadWarning.File2' 
                                            params={{ firstName: filerName?.split(' ')[0] }} /></li>
                                    </ul>
                                </div>
                            </div>
                        )}
                        {authorizationInfo.signaturePage && <Button
                            elementId='document-review-reset-signature-page-btn'
                            labelKey='DocumentReviewTask.ResetSignaturePage'
                            buttonStyles='secondary center'
                            onClick={() => handleResetSignaturePageClick()}
                            disabled={!isInProgress} /> }
                        <div className='upload-info-section'>
                            <p className='p2'>
                                <HrbTranslate text='DocumentReviewTask.UploadedOn' />
                            </p>
                            <p className='p2'>
                                {authorizationInfo.signaturePageUploadTimestamp ? <DateTime boldValue={true} value={authorizationInfo.signaturePageUploadTimestamp} dateAndTime/>
                                                                    : '---'
                                }
                            </p>
                        </div>
                        <DoxUploader ticket={ticket} show={uploaderOpen}
                            layoutType={'desktop'} onUploadsComplete={onUploadsComplete} onClose={onCloseUploader} isSignaturePageContext={true} filerName={filerName} />
                    </>}
                </>)}
            </>)}
            <WorkflowTaskModal isOpen={showConfirmAuthorizationModal} titleKey={modalConfig.titleKey}
                    contentKeys={modalConfig.contentKeys}
                    primaryButtonKey={modalConfig.actionKey}
                    onCancel={handleCancel}
                    onConfirm={handleConfirmation}
                    action={modalConfig.action}
                    showWarning={false} />
        </AccordionWithStatus>
    );
}

DocumentReviewAuthorizationTask.whyDidYouRender = false;

DocumentReviewAuthorizationTask.propTypes = {
    elementId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    translateTitle: PropTypes.bool,
    groupLabel: PropTypes.string,
    isInProgress: PropTypes.bool.isRequired,
    filer: PropTypes.string.isRequired,
    filerName: PropTypes.string,
    authorizationInfo: PropTypes.object,
    saveAuthorizationData: PropTypes.func.isRequired,
    completeAuthorization: PropTypes.func.isRequired,
    signaturePageWorkflowTask: PropTypes.string.isRequired,
    resetSignaturePage: PropTypes.func.isRequired
}

export default DocumentReviewAuthorizationTask